import { alpha, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import RouterLink from "src/hooks/RouterLink";
import Logo from "src/components/Logo";
import Iconify from "src/components/Iconify";
import { useHref } from "react-router";

const year = new Date().getFullYear();
const LINKS = [
  {
    headline: "Navigation",
    children: [
      { name: "À Propos", href: "#" },
      { name: "Contactez-nous", href: "/contact" },
      { name: "FAQs", href: "/faq" },
    ],
  },
  {
    headline: "Légal",
    children: [{ name: "Cadre juridique", href: "/mentions-legales" }],
  },
  {
    headline: "Contact",
    children: [
      { name: "+33 1 83 75 63 26", href: "tel:+33183756326" },
      { name: "contact@honest-inn.com", href: "mailto:contact@honest-inn.com" },
    ],
  },
];

export default function Footer() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        bgcolor: isDarkMode ? "#171717" : "#FFF7EA",
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 2, sm: 0 }, // Add horizontal padding for mobile
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="flex-start">
          {/* Logo Section */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
              <Logo
                sx={{
                  mb: 2,
                  maxWidth: { xs: "160px", sm: "180px", md: "200px" },
                  mx: { xs: "auto", sm: 0 },
                }}
              />

              {/* App Download Buttons */}
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: "text.primary",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  fontWeight: 500,
                }}
              >
                Téléchargez notre app !
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: { xs: "center", sm: "flex-start" },
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <IconButton
                  onClick={() =>
                    window.open(
                      "https://play.google.com/store/apps/details?id=com.honest_inn.honest_inn",
                      "_blank"
                    )
                  }
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.8),
                      transform: "scale(1.05)",
                      transition: "transform 0.2s ease",
                    },
                    width: { xs: "32px", sm: "36px" },
                    height: { xs: "32px", sm: "36px" },
                    borderRadius: "8px",
                  }}
                >
                  <Iconify
                    icon="logos:google-play-icon"
                    width={{ xs: 16, sm: 20 }}
                    height={{ xs: 16, sm: 20 }}
                  />
                </IconButton>
                <IconButton
                  onClick={() =>
                    window.open(
                      "https://apps.apple.com/fr/app/honest-inn/id6535696804",
                      "_blank"
                    )
                  }
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.8),
                      transform: "scale(1.05)",
                      transition: "transform 0.2s ease",
                    },
                    width: { xs: "32px", sm: "36px" },
                    height: { xs: "32px", sm: "36px" },
                    borderRadius: "8px",
                  }}
                >
                  <Iconify
                    icon="logos:apple-app-store"
                    width={{ xs: 16, sm: 20 }}
                    height={{ xs: 16, sm: 20 }}
                  />
                </IconButton>
              </Stack>
            </Box>
          </Grid>

          {/* Navigation Links */}
          {LINKS.map((list, index) => (
            <Grid item xs={12} sm={6} md={2.25} key={list.headline}>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography
                  component="div"
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    mb: { xs: 1.5, sm: 2 },
                  }}
                >
                  {list.headline}
                </Typography>
                <Stack spacing={{ xs: 1, sm: 1.5 }}>
                  {list.children.map((link) => (
                    <Link
                      key={link.name}
                      href={link.href}
                      color="text.secondary"
                      variant="body2"
                      sx={{
                        textDecoration: "none",
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        wordBreak: "break-word", // Handle long text on mobile
                        "&:hover": {
                          color: theme.palette.primary.main,
                          textDecoration: "none",
                        },
                      }}
                      target={
                        link.href.startsWith("http") ? "_blank" : undefined
                      } // optional, for web links
                      rel={
                        link.href.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              </Box>
            </Grid>
          ))}

          {/* Social Media Icons */}
          <Grid item xs={12} sm={12} md={1.5}>
            <Box sx={{ textAlign: { xs: "center", md: "right" } }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: { xs: "center", md: "flex-end" },
                  mt: { xs: 2, sm: 0 },
                }}
              >
                <IconButton
                  onClick={() =>
                    window.open(
                      "https://www.linkedin.com/company/honest-inn/",
                      "_blank"
                    )
                  }
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "#0077B5",
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                    p: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Iconify
                    icon="eva:linkedin-fill"
                    width={{ xs: 18, sm: 20 }}
                    height={{ xs: 18, sm: 20 }}
                  />
                </IconButton>
                <IconButton
                  onClick={() =>
                    window.open(
                      "https://www.facebook.com/p/Honest-Inn-100076855702366/",
                      "_blank"
                    )
                  }
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "#1877F2",
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                    p: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Iconify
                    icon="eva:facebook-fill"
                    width={{ xs: 18, sm: 20 }}
                    height={{ xs: 18, sm: 20 }}
                  />
                </IconButton>
                <IconButton
                  onClick={() => (window.location.href = "")}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "#000000",
                      transform: "scale(1.1)",
                      transition: "all 0.2s ease",
                    },
                    p: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "16px" },
                      fontWeight: "bold",
                    }}
                  >
                    X
                  </Typography>
                </IconButton>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: { xs: 3, sm: 4 }, opacity: 0.3 }} />

        {/* Copyright */}
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
            textAlign: "center",
          }}
        >
          © {year}. Tous droits réservés
        </Typography>
      </Container>
    </Box>
  );
}
